import {
  AdminCustomSelect,
  AdminInput,
  AdminLocationsPickerModal,
  AdminPhoneInput,
  api,
  BrandType,
  Button,
  Div,
  ErrorToast,
  isAdmin,
  ModalPortal,
  SuccessToast,
  reportError,
} from '@vgrubs/components';
import { SimpleLocationEntity } from 'components/src/types/location.types';
import { UserRole } from 'components/src/types/user.types';
import { useState } from 'react';

const adminRoles = [
  { name: 'Admin', value: UserRole.admin },
  { name: 'Support', value: UserRole.support },
  { name: 'Marketing', value: UserRole.marketing },
  { name: 'Partner', value: UserRole.partner },
  { name: 'Manager', value: UserRole.manager },
  { name: 'Brand Owner', value: UserRole.brandOwner },
];

const partnerRoles = [
  { name: 'Partner', value: UserRole.partner },
  { name: 'Manager', value: UserRole.manager },
  { name: 'Staff', value: UserRole.staff },
];

const options = isAdmin ? adminRoles : partnerRoles;

const brandTypes = [
  { name: 'TODC', value: BrandType.TDOC },
  { name: 'VPC', value: BrandType.VPC },
  { name: 'VDC', value: BrandType.VDC },
  { name: 'FJ', value: BrandType.FJ },
  { name: 'The Licking Brand', value: BrandType.TLB },
];

export const UsersAndAccessAddModal = ({
  modalIsOpen,
  setModalIsOpen,
  setLoading,
  onCreateSuccess,
  refetch,
}: {
  modalIsOpen: boolean;
  setModalIsOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
  onCreateSuccess?: () => void;
  refetch: () => void;
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState(options[0].value);
  const [selectedLocations, setSelectedLocations] = useState<
    SimpleLocationEntity[]
  >([]);
  const [brandTypesSelected, setBrandTypesSelected] = useState<
    BrandType[] | null
  >([]);

  const hasLocations =
    role !== UserRole.admin &&
    role !== UserRole.support &&
    role !== UserRole.marketing &&
    role !== UserRole.brandOwner;

  async function handleVerifyLocation() {
    if (!firstName) {
      ErrorToast('Please enter a first name');
      return;
    }
    if (!lastName) {
      ErrorToast('Please enter a last name');
      return;
    }
    if (!email) {
      ErrorToast('Please enter an email');
      return;
    }
    if (!phoneNumber) {
      ErrorToast('Please enter a phone number');
      return;
    }
    if (!role) {
      ErrorToast('Please select a role');
      return;
    }

    const locationIds = selectedLocations.map(({ id }) => id);

    const body = {
      firstName,
      lastName,
      phoneNumber,
      role: role === UserRole.brandOwner ? undefined : role,
      email,
      locationIds:
        role === UserRole.brandOwner
          ? undefined
          : hasLocations
          ? locationIds
          : null,
      types: role === UserRole.brandOwner ? brandTypesSelected : undefined,
    };

    try {
      setLoading(true);
      const { data } = await api.post(
        role === UserRole.brandOwner ? `/v1/users/brand-owner` : `/v1/users`,
        body
      );
      SuccessToast(data.message);
      onCreateSuccess?.();
      setModalIsOpen(false);

      setSelectedLocations([]);
      setRole(options[0].value);
      setPhoneNumber('');
      setEmail('');
      setLastName('');
      setFirstName('');
      refetch();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalPortal
      isOpen={!!modalIsOpen}
      closeModal={() => setModalIsOpen(false)}
      logo={false}
      header="Add User"
      overflow="visible"
    >
      <Div $flex $column $gap="20px" $gapSM="16px">
        <AdminInput
          label="First Name"
          placeholder="Enter here"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          $textCenter
        />
        <AdminInput
          label="Last Name"
          placeholder="Enter here"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          $textCenter
        />
        <AdminInput
          label="Email"
          placeholder="Enter here"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          $textCenter
        />
        <AdminPhoneInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          label="Phone"
          $textCenter
        />

        <AdminCustomSelect
          value={role}
          onChange={(value) => setRole(value as UserRole)}
          label="Role"
          $textCenter
          $shadowDesign={false}
          options={options}
        />

        {role === UserRole.brandOwner && (
          <AdminCustomSelect
            value={brandTypesSelected}
            options={brandTypes}
            onChange={(value) =>
              setBrandTypesSelected(value as BrandType[] | null)
            }
            placeholder="Select brand type"
            label="Brand Type"
            $textCenter
            multiSelect
            $shadowDesign={false}
          />
        )}

        {hasLocations && (
          <AdminLocationsPickerModal
            selectedLocations={selectedLocations}
            setSelectedLocations={setSelectedLocations}
            handleSubmit={() => null}
            label="Location"
            $textCenter
          />
        )}
      </Div>

      <Button
        $selected
        $w="100%"
        $mt="50"
        $mtSM="30"
        onClick={handleVerifyLocation}
      >
        Add
      </Button>
    </ModalPortal>
  );
};
